import moment from "moment";

import TrainingSummaryList from "issara-sdk/apis/TrainingSummaryList_apps_HRM";
import TrainingSummaryUpdate from "issara-sdk/apis/TrainingSummaryUpdate_apps_HRM";

import TrainingSummarySerializer from "issara-sdk/types/TrainingSummarySerializer_apps_HRM";

import getPdfMake from "react-lib/appcon/common/pdfMake";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import FormTrainingKPIReport, { SummaryKPIType } from "../pdfForm/FormTrainingKPIReport";

import { MONTH_OPTION } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = { PRINT_REPORT: "PRINT_REPORT", SAVE: "SAVE", SEARCH: "SEARCH" } as const;

export const SEQUENCE_NAME = "SummaryTrainingKPI" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  SummaryTrainingKPISequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: FilterType;
    items: TrainingSummaryType[];
  }> | null;
  masterOptions: MasterOptions;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey" | "selectedDivision"
>;

export type TrainingSummaryType = TrainingSummarySerializer & {
  is_editing?: boolean;
  result_course_per_month: "FAIL" | "PASS";
  result_hour_per_month: "FAIL" | "PASS";
  result_score_per_month: "FAIL" | "PASS";
};

export type MasterOptions = MasterMapOptions<typeof Masters>;

type FilterType = Partial<{ division_id: number | null; month: number; year: number }>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  SummaryTrainingKPISequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["positionAll", {}],
  ["division", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  const now = moment();
  const year = now.year();
  const month = now.month() + 1;

  controller.setState(
    {
      SummaryTrainingKPISequence: {
        ...state.SummaryTrainingKPISequence,
        sequenceIndex: "Action",
        filter: { division_id: state.selectedDivision?.id, month, year },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.PRINT_REPORT]: HandlePrintReport,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler = async (controller) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const items = await GetTrainingSummaryList(controller);

  state = controller.getState();

  controller.setState({
    SummaryTrainingKPISequence: { ...state.SummaryTrainingKPISequence, items },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const [, error] = await TrainingSummaryUpdate.post({
    apiToken: controller.apiToken,
    data: { items: state.SummaryTrainingKPISequence?.items ?? [] },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  HandleSearch(controller);

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
  });
};

const HandlePrintReport: Handler = async (controller) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.LOADING },
  });

  const filter = state.SummaryTrainingKPISequence?.filter ?? {};

  const options = state.masterOptions?.division ?? [];

  const foundDivision = options.find((option) => Number(option.value) === filter.division_id);
  const foundMonth = MONTH_OPTION.find((option) => option.value === filter.month);

  const dateRange = `${foundMonth?.text || ""} ${Number(filter.year) + 543}`;

  const items = await GetTrainingSummaryList(controller);

  const docDef = await FormTrainingKPIReport({
    fields: items,
    params: {
      date_range: dateRange,
      division_name: foundDivision?.text || "",
      printed_date: moment().format("DD/MM/YYYY, HH:mm"),
    },
    summary: calculateSummaryKPI(items),
  });

  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.SUCCESS },
  });

  pdf.open();
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetTrainingSummaryList: Handler<unknown, Promise<TrainingSummaryType[]>> = async (
  controller
) => {
  const state = controller.getState();

  const filter = state.SummaryTrainingKPISequence?.filter ?? {};

  const [result] = await TrainingSummaryList.list({
    apiToken: controller.apiToken,
    params: { ...filter },
  });

  const items: TrainingSummaryType[] = result?.items || [];

  return items.map((item) => ({
    ...item,
    result_course_per_month:
      Number(item.summary_course_per_month || 0) >= Number(item.minimum_course_per_month || 0)
        ? "PASS"
        : "FAIL",
    result_hour_per_month:
      Number(item.summary_hour_per_month || 0) >= Number(item.minimum_hour_per_month || 0)
        ? "PASS"
        : "FAIL",
    result_score_per_month:
      Number(item.score || 0) >= Number(item.minimum_score_per_month || 0) ? "PASS" : "FAIL",
  }));
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */

// คำนวณ Summary KPI จากข้อมูลพนักงาน
const calculateSummaryKPI = (data: TrainingSummaryType[]): SummaryKPIType[] => {
  if (data.length === 0) {
    const emptyResult = {
      account_for_course: 0,
      account_for_hour: 0,
      account_for_score: 0,
      training_course: 0,
      training_hour: 0,
      training_score: 0,
    };

    return [
      { result: "PASS", ...emptyResult },
      { result: "FAIL", ...emptyResult },
    ];
  }

  const totalEmployees = data.length;

  // นับและคำนวณเปอร์เซ็นต์
  const calculateMetrics = (
    field: keyof Pick<
      TrainingSummaryType,
      "result_course_per_month" | "result_hour_per_month" | "result_score_per_month"
    >
  ) => {
    // นับจำนวนคนที่ผ่าน (PASS) และไม่ผ่าน (FAIL) ในเกณฑ์ที่ระบุ
    const pass = data.filter((emp) => emp[field] === "PASS").length;
    const fail = data.filter((emp) => emp[field] === "FAIL").length;

    // คำนวณเปอร์เซ็นต์ตามสูตร: (จำนวนคน × 100) ÷ จำนวนพนักงานทั้งหมด
    // Math.round(...* 100) / 100 = ปัดเศษทศนิยม 2 ตำแหน่ง
    const passPercent = Math.round(((pass * 100) / totalEmployees) * 100) / 100;
    const failPercent = Math.round(((fail * 100) / totalEmployees) * 100) / 100;

    return {
      fail,
      failPercent,
      pass,
      passPercent,
    };
  };

  // คำนวณผลลัพธ์สำหรับแต่ละเกณฑ์
  const course = calculateMetrics("result_course_per_month");
  const hour = calculateMetrics("result_hour_per_month");
  const score = calculateMetrics("result_score_per_month");

  return [
    {
      account_for_course: course.passPercent,
      account_for_hour: hour.passPercent,
      account_for_score: score.passPercent,
      result: "PASS",
      training_course: course.pass,
      training_hour: hour.pass,
      training_score: score.pass,
    },
    {
      account_for_course: course.failPercent,
      account_for_hour: hour.failPercent,
      account_for_score: score.failPercent,
      result: "FAIL",
      training_course: course.fail,
      training_hour: hour.fail,
      training_score: score.fail,
    },
  ];
};
